// frontend/src/pages/SessionPage.jsx
// Patent Council session page. Each SSE connection runs ONE round.
// Between rounds the page surfaces an HIL pause panel (comment + visibility);
// an Interrupt button is available mid-round; cap-reached and not-viable
// verdicts show their own panels.

import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';

let mermaidPromise = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(mod => {
      mod.default.initialize({ startOnLoad: false, theme: 'dark', securityLevel: 'loose' });
      return mod.default;
    });
  }
  return mermaidPromise;
}

import {
  getProject,
  getProjectMessages,
  getProjectIssues,
  getProjectChecklist,
  getProjectDraft,
  openSessionStream,
  extendRoundCap,
  requestInterrupt,
  postRoundComment
} from '../utils/api';

const AGENT_COLORS = {
  lex: 'text-blue-300',
  nick: 'text-green-300',
  sparks: 'text-amber-300',
  euler: 'text-purple-300',
  argus: 'text-cyan-300',
  mindblower: 'text-fuchsia-300',
  max: 'text-red-300',
  wendy: 'text-orange-300',
  nullius: 'text-violet-300',
  countex: 'text-sky-300',
  wanda: 'text-yellow-300',
  patricia: 'text-emerald-300',
  archie: 'text-lime-300',
  eligia: 'text-teal-300',
  vera: 'text-rose-300'
};
const AGENT_NAMES = {
  lex: 'Lex Quill',
  nick: 'Nick Blueprint',
  sparks: 'Dr. Marie Sparks',
  euler: 'Euler Prime',
  argus: 'Argus',
  mindblower: 'Mindblower',
  max: 'Max Loophole',
  wendy: 'Wendy Wrench',
  nullius: 'Dr. Nullius Hypothesis',
  countex: 'Count Ample',
  wanda: 'Wanda Wayback',
  patricia: 'PATRICIA',
  archie: 'ARCHIE',
  eligia: 'Eligia Prism',
  vera: 'Vera Accord'
};

export default function SessionPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [score, setScore] = useState(0);
  const [recommendation, setRecommendation] = useState(null);
  const [effectiveCap, setEffectiveCap] = useState(null);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [activeRoundNum, setActiveRoundNum] = useState(null);
  const [activePhase, setActivePhase] = useState(null);
  const [interRoundPanel, setInterRoundPanel] = useState(null);
  const [pausedPanel, setPausedPanel] = useState(null);
  const [capPanel, setCapPanel] = useState(null);
  const [notViablePanel, setNotViablePanel] = useState(null);
  const [viabilityChecking, setViabilityChecking] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [interruptPending, setInterruptPending] = useState(false);
  const [inspectorTab, setInspectorTab] = useState(null); // null | 'seed' | 'issues' | 'checklist' | 'design'
  const [issues, setIssues] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [designAround, setDesignAround] = useState(null);
  const [rateLimit, setRateLimit] = useState(null);
  const [phase0, setPhase0] = useState(null);
    // null | { stage: 'concept_brief'|'research'|'initial_draft'|'idle', label }
  const [conceptBrief, setConceptBrief] = useState(null);
  const [researchReport, setResearchReport] = useState(null);
  const [draft, setDraft] = useState(null);
  const [initialDraftPanel, setInitialDraftPanel] = useState(null);
  const [draftRevising, setDraftRevising] = useState(false);
  const [discussion, setDiscussion] = useState(null);
    // null | { stage: 'triage'|'framing'|'proposal'|'synthesis'|'complete', issueIndex, total, label }
  const [error, setError] = useState('');

  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const hasAutoStarted = useRef(false);

  useEffect(() => {
    reloadAll();
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, [projectId]);

  useEffect(() => {
    const autostart = searchParams.get('autostart');
    if (autostart === 'true' && project && !hasAutoStarted.current && !isStreamActive && project.status === 'ACTIVE') {
      hasAutoStarted.current = true;
      setTimeout(() => startRound(), 500);
    }
  }, [project, searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function reloadAll() {
    try {
      const [p, m, iss, cl, d] = await Promise.all([
        getProject(projectId),
        getProjectMessages(projectId),
        getProjectIssues(projectId).catch(() => []),
        getProjectChecklist(projectId).catch(() => []),
        getProjectDraft(projectId).catch(() => null)
      ]);
      setProject(p);
      setMessages(m);
      setIssues(iss);
      setChecklist(cl);
      if (d) setDraft(d);
      if (p.conceptBrief) setConceptBrief(p.conceptBrief);
      if (p.researchReport) setResearchReport(p.researchReport);
      setCurrentRound(p.currentRound);
      setScore(p.convergenceScore);
      setRecommendation(p.filingRecommendation);
      if (p.status === 'CAPPED' && p.viabilityVerdict) {
        setCapPanel({ verdict: p.viabilityVerdict, score: p.convergenceScore, recommendation: p.filingRecommendation });
      } else if (p.status === 'NOT_VIABLE' && p.viabilityVerdict) {
        setNotViablePanel({ verdict: p.viabilityVerdict, score: p.convergenceScore });
      } else if (p.roundPausedAt) {
        setPausedPanel({ state: p.roundPausedAt });
      }
    } catch (err) {
      setError(err.message);
    }
  }

  const startRound = () => {
    setIsStreamActive(true);
    setInterRoundPanel(null);
    setPausedPanel(null);
    setCapPanel(null);
    setNotViablePanel(null);
    setInitialDraftPanel(null);
    setRateLimit(null);
    setDiscussion(null);
    setError('');
    setInterruptPending(false);

    const es = openSessionStream(projectId, {
      onRoundStart: (data) => {
        setActiveRoundNum(data.roundNum);
        setActivePhase(data.phase);
        setEffectiveCap(data.effectiveCap);
      },
      onRoundResume: (data) => {
        setActiveRoundNum(data.roundNum);
        setActivePhase(data.phase);
        setEffectiveCap(data.effectiveCap);
      },
      onAgentMessage: (data) => {
        setActivePhase(data.phase);
        setRateLimit(null);
        setMessages(prev => [...prev, {
          id: Date.now() + Math.random(),
          agentId: data.agentId, side: data.side, specialty: data.specialty,
          content: data.content, roundNum: data.roundNum, phase: data.phase
        }]);
      },
      onRateLimitWaiting: (data) => setRateLimit(data),
      onViabilityCheck: (_data) => setViabilityChecking(true),
      onNormalizing: () => setNormalizing(true),
      onNormalizationComplete: () => { setNormalizing(false); reloadAll(); },
      onNormalizationFailed: () => setNormalizing(false),
      onIssueAdded: (data) => setIssues(prev => [...prev, data]),
      onChecklist: (data) => { if (Array.isArray(data.items)) setChecklist(data.items.map(x => ({ item_num: x.item_id, item_text: x.item_text, result: x.result, reason: x.reason }))); },
      onDesignAround: (data) => setDesignAround(data),
      onRoundComplete: (data) => {
        setIsStreamActive(false);
        setCurrentRound(data.roundNum);
        setScore(data.score);
        setRecommendation(data.recommendation);
        setEffectiveCap(data.effectiveCap);
        setInterRoundPanel({
          roundNum: data.roundNum, score: data.score, issues: data.issues,
          effectiveCap: data.effectiveCap, recommendation: data.recommendation
        });
      },
      onPaused: (data) => {
        setIsStreamActive(false);
        setInterruptPending(false);
        setPausedPanel({ state: data.state });
      },
      onCapReached: (data) => {
        setIsStreamActive(false);
        setViabilityChecking(false);
        setCurrentRound(prev => prev);
        setScore(data.score);
        setEffectiveCap(data.effectiveCap);
        setRecommendation(data.recommendation);
        setCapPanel({ verdict: data.verdict, recommendation: data.recommendation, score: data.score, issues: data.issues });
        reloadAll();
      },
      onNotViable: (data) => {
        setIsStreamActive(false);
        setViabilityChecking(false);
        setScore(data.score);
        setEffectiveCap(data.effectiveCap);
        setRecommendation('NOT VIABLE');
        setNotViablePanel({ verdict: data.verdict, score: data.score, issues: data.issues });
        reloadAll();
      },
      onConverged: (data) => {
        setIsStreamActive(false);
        setRecommendation(data.recommendation);
        setScore(data.score);
        reloadAll();
      },
      onError: (data) => {
        setIsStreamActive(false);
        setViabilityChecking(false);
        setError(data?.message || 'Stream error occurred');
      },
      onConceptBriefBuilding: () => setPhase0({ stage: 'concept_brief', label: 'Building concept brief...' }),
      onConceptBriefComplete: (data) => {
        setConceptBrief(data?.brief || null);
        setPhase0({ stage: 'concept_brief', label: 'Concept brief complete.', done: true });
      },
      onConceptBriefFailed: (data) => {
        setPhase0(null);
        setError(data?.message || 'Concept brief failed');
      },
      onResearching: () => setPhase0({ stage: 'research', label: 'Researching prior art and design-around space...' }),
      onResearchComplete: (data) => {
        setPhase0({ stage: 'research', label: `Research complete: ${data?.categoryCount || 0} prior-art categories, ${data?.designAroundCount || 0} design-arounds.`, done: true });
      },
      onResearchFailed: (data) => {
        setPhase0(null);
        setError(data?.message || 'Research failed');
      },
      onInitialDraftBuilding: () => setPhase0({ stage: 'initial_draft', label: 'Building initial patent draft...' }),
      onDiagramsBuilding: () => setPhase0({ stage: 'initial_draft', label: 'Generating diagrams...' }),
      onDiagramsReady: () => setPhase0({ stage: 'initial_draft', label: 'Diagrams ready.', done: true }),
      onDiagramsFailed: () => setPhase0({ stage: 'initial_draft', label: 'Diagrams failed; draft is still usable.', done: true }),
      onInitialDraftReady: (data) => {
        setIsStreamActive(false);
        setPhase0(null);
        if (data?.draft) {
          const d = {
            ...data.draft,
            claims: typeof data.draft.claims === 'string' ? JSON.parse(data.draft.claims) : data.draft.claims
          };
          setDraft(d);
          setInitialDraftPanel({ draft: d });
        }
      },
      onInitialDraftFailed: (data) => {
        setIsStreamActive(false);
        setPhase0(null);
        setError(data?.message || 'Initial draft failed');
      },
      onDraftRevising: () => setDraftRevising(true),
      onDraftRevised: (data) => {
        setDraftRevising(false);
        if (data?.draft) {
          setDraft({
            ...data.draft,
            claims: typeof data.draft.claims === 'string' ? JSON.parse(data.draft.claims) : data.draft.claims
          });
        }
      },
      onDraftRevisionFailed: () => setDraftRevising(false),
      onTriageStart: (data) => setDiscussion({ stage: 'triage', label: `Argus is triaging ${data.issueCount} open issue(s)...` }),
      onTriageComplete: (data) => setDiscussion({ stage: 'triage', label: `Triage complete: ${data.triage?.length || 0} issue(s) ordered.`, total: data.triage?.length || 0, done: true }),
      onDiscussionStart: (data) => setDiscussion({ stage: 'framing', issueIndex: data.issueIndex, total: data.total, label: `Issue ${data.issueIndex + 1}/${data.total}: ${data.issue?.description || ''}` }),
      onDiscussionFraming: (data) => setDiscussion(prev => prev ? { ...prev, stage: 'proposal', issueIndex: data.issueIndex, label: `Issue ${data.issueIndex + 1}: writers proposing amendments...` } : prev),
      onDiscussionProposal: (data) => setDiscussion(prev => prev ? { ...prev, stage: 'proposal', issueIndex: data.issueIndex, label: `Issue ${data.issueIndex + 1}: ${data.agentName} proposed.` } : prev),
      onDiscussionSynthesis: (data) => setDiscussion(prev => prev ? { ...prev, stage: 'synthesis', issueIndex: data.issueIndex, label: `Issue ${data.issueIndex + 1}: Mindblower synthesized.` } : prev),
      onDiscussionsComplete: (data) => setDiscussion({ stage: 'complete', label: `Discussion complete (${data.discussionCount} issue${data.discussionCount === 1 ? '' : 's'}). Adversarial review starting...`, done: true }),
      onDiscussionSkipped: (data) => setDiscussion({ stage: 'complete', label: data.reason || 'No blocking issues to discuss.', done: true })
    });

    eventSourceRef.current = es;
  };

  const onInterruptClick = async () => {
    try {
      setInterruptPending(true);
      await requestInterrupt(projectId);
    } catch (err) {
      setInterruptPending(false);
      setError(err.message);
    }
  };

  const onContinueInterRound = async ({ comment, visibility }) => {
    try {
      if (comment && comment.trim()) {
        await postRoundComment(projectId, { comment: comment.trim(), visibility, round: currentRound });
      }
      const cap = interRoundPanel?.effectiveCap ?? effectiveCap;
      if (cap != null && currentRound >= cap) {
        await extendRoundCap(projectId, { add: 5 });
      }
      startRound();
    } catch (err) {
      setError(err.message);
    }
  };

  const onResumePaused = async ({ comment, visibility }) => {
    try {
      if (comment && comment.trim()) {
        await postRoundComment(projectId, { comment: comment.trim(), visibility, round: currentRound });
      }
      startRound();
    } catch (err) {
      setError(err.message);
    }
  };

  const onExtendCap = async ({ add, comment, visibility }) => {
    try {
      await extendRoundCap(projectId, { add });
      if (comment && comment.trim()) {
        await postRoundComment(projectId, { comment: comment.trim(), visibility, round: currentRound });
      }
      setCapPanel(null);
      setNotViablePanel(null);
      startRound();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const capDisplay = effectiveCap != null ? effectiveCap : (project.maxRoundsCap ?? '—');

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Link to="/dashboard" className="text-sm text-gray-400 hover:text-white">← Dashboard</Link>
              <h1 className="text-xl font-bold">{project.inventionTitle}</h1>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-sm">
                Round{' '}
                <span className="font-bold">
                  {activeRoundNum || currentRound}
                </span>
                {' / '}
                <span className="text-gray-400">{capDisplay}</span>
              </div>
              <div className="text-sm">Score: <span className="font-bold">{score}</span></div>
              {recommendation && (
                <div className={`text-sm font-medium ${recommendation === 'NOT VIABLE' ? 'text-red-400' : 'text-green-400'}`}>
                  {recommendation}
                </div>
              )}
              {isStreamActive && (
                <button
                  onClick={onInterruptClick}
                  disabled={interruptPending}
                  className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-900 disabled:cursor-not-allowed px-3 py-1.5 rounded-md text-xs font-medium"
                >
                  {interruptPending ? 'Pausing at next agent...' : 'Interrupt'}
                </button>
              )}
              <InspectorTabsButton
                tab={inspectorTab}
                setTab={setInspectorTab}
                showSeed={project.projectMode === 'seeded'}
                issueCount={issues.length}
              />

            </div>
          </div>
        </div>
      </div>

      <InspectorDrawer
        activeTab={inspectorTab}
        onClose={() => setInspectorTab(null)}
        onTabChange={setInspectorTab}
        project={project}
        issues={issues}
        checklist={checklist}
        designAround={designAround}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-900 text-white p-4 rounded-lg mb-4" role="alert">
            {error}
          </div>
        )}

        {normalizing && (
          <div className="bg-blue-900/30 border border-blue-700 text-blue-200 rounded-lg p-4 mb-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-300"></div>
            <span>Analyzing seed document (one-time pre-round pass)...</span>
          </div>
        )}

        {rateLimit && (
          <div className="bg-amber-900/30 border border-amber-700 text-amber-200 rounded-lg p-4 mb-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-300"></div>
            <span>
              Anthropic {rateLimit.kind === 'rate_limit' ? 'rate-limited' : `transient ${rateLimit.kind}`} on{' '}
              <strong>{AGENT_NAMES[rateLimit.agentId] || rateLimit.agentName || rateLimit.agentId}</strong>.
              Waiting ~{rateLimit.waitSeconds}s before retry (attempt {rateLimit.attempt}/{rateLimit.maxAttempts}).
            </span>
          </div>
        )}

        {phase0 && (
          <div className="bg-indigo-900/30 border border-indigo-700 text-indigo-100 rounded-lg p-4 mb-4 space-y-2">
            <div className="flex items-center space-x-3">
              {!phase0.done && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-300"></div>}
              <span className="font-medium">Phase 0: {phase0.label}</span>
            </div>
            <div className="text-xs text-indigo-300 flex gap-4">
              <span className={phase0.stage === 'concept_brief' || conceptBrief ? 'text-indigo-100' : 'text-indigo-400'}>
                {conceptBrief ? '✓' : phase0.stage === 'concept_brief' ? '◐' : '○'} Concept brief
              </span>
              <span className={phase0.stage === 'research' || researchReport ? 'text-indigo-100' : 'text-indigo-400'}>
                {researchReport ? '✓' : phase0.stage === 'research' ? '◐' : '○'} Prior-art research
              </span>
              <span className={phase0.stage === 'initial_draft' ? 'text-indigo-100' : 'text-indigo-400'}>
                {phase0.stage === 'initial_draft' ? '◐' : '○'} Initial draft
              </span>
            </div>
          </div>
        )}

        {draftRevising && (
          <div className="bg-purple-900/30 border border-purple-700 text-purple-100 rounded-lg p-4 mb-4 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-300"></div>
            <span>Synthesizing the next draft version from this round's amendments and findings...</span>
          </div>
        )}

        {discussion && (
          <div className="bg-cyan-900/30 border border-cyan-700 text-cyan-100 rounded-lg p-4 mb-4 space-y-2">
            <div className="flex items-center space-x-3">
              {!discussion.done && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-300"></div>}
              <span className="font-medium">Council Discussion: {discussion.label}</span>
            </div>
            {discussion.total > 0 && discussion.stage !== 'complete' && (
              <div className="text-xs text-cyan-300">
                Stage: {discussion.stage}{discussion.issueIndex != null ? ` · issue ${discussion.issueIndex + 1} of ${discussion.total}` : ''}
              </div>
            )}
          </div>
        )}

        <div className="space-y-4 mb-8">
          {messages.map((msg) => (
            <MessageCard key={msg.id} msg={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>

        {isStreamActive && (
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 text-gray-300">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>
                Council in session — round {activeRoundNum || currentRound}
                {activePhase ? ` · ${activePhase} phase` : ''}
              </span>
            </div>
            {viabilityChecking && (
              <div className="mt-3 text-xs text-amber-300">Round cap reached — evaluating viability...</div>
            )}
          </div>
        )}

        {!isStreamActive && !interRoundPanel && !pausedPanel && !capPanel && !notViablePanel && !initialDraftPanel &&
          project.status === 'ACTIVE' && (
            <div className="text-center">
              <button
                onClick={startRound}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
              >
                {project.initialDraftStatus !== 'complete'
                  ? 'Start Phase 0: Concept Brief, Research, and Initial Draft'
                  : currentRound === 0
                    ? 'Start Adversarial Review (Round 1)'
                    : 'Continue Session'}
              </button>
            </div>
          )}

        {initialDraftPanel && (
          <InitialDraftPanel
            draft={initialDraftPanel.draft}
            conceptBrief={conceptBrief}
            researchReport={researchReport}
            onContinue={() => { setInitialDraftPanel(null); startRound(); }}
          />
        )}

        {interRoundPanel && (
          <InterRoundPanel
            state={interRoundPanel}
            onContinue={onContinueInterRound}
          />
        )}

        {pausedPanel && (
          <PausedPanel
            state={pausedPanel.state}
            onResume={onResumePaused}
          />
        )}

        {capPanel && !notViablePanel && (
          <CapReachedPanel
            state={capPanel}
            onExtend={onExtendCap}
            onFinalize={() => {
              window.location.href = `/results/${projectId}`;
            }}
          />
        )}

        {notViablePanel && (
          <NotViablePanel
            state={notViablePanel}
            onExtend={onExtendCap}
            onAccept={() => {
              window.location.href = `/results/${projectId}`;
            }}
          />
        )}

        {project.status === 'CONVERGED' && (
          <div className="text-center bg-green-900/30 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-2">Session Complete — Converged</h2>
            <p className="text-gray-300 mb-4">{recommendation}</p>
            <Link
              to={`/results/${projectId}`}
              className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700"
            >
              View Full Results
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

const SECTION_LABELS = {
  problem: 'The Problem',
  invention: 'The Invention',
  prior_art_inventor: 'Prior Art — Inventor',
  prior_art_external: 'Prior Art — External',
  embodiments: 'Embodiments',
  expected_expansion: 'Expected Expansion',
  non_claims: 'Non-Claims',
  inventor_intent: 'Inventor Intent'
};

function InspectorTabsButton({ tab, setTab, showSeed, issueCount }) {
  const btn = (id, label, extra) => (
    <button
      key={id}
      onClick={() => setTab(tab === id ? null : id)}
      aria-pressed={tab === id}
      className={`px-2.5 py-1 text-xs font-medium rounded-md border ${
        tab === id
          ? 'bg-gray-700 border-gray-500 text-white'
          : 'bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800'
      }`}
    >
      {label}{extra}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-gray-500 mr-1">Inspector:</span>
      {showSeed && btn('seed', 'Seed')}
      {btn('issues', 'Issues', issueCount ? ` (${issueCount})` : '')}
      {btn('checklist', 'Checklist')}
      {btn('design', 'Design-Around')}
    </div>
  );
}

function InspectorDrawer({ activeTab, onClose, onTabChange, project, issues, checklist, designAround }) {
  if (!activeTab) return null;
  const titles = { seed: 'Seed Reference', issues: 'Issues', checklist: '16-Item Checklist', design: 'Design-Around Analysis' };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-gray-950 border-l border-gray-700 shadow-2xl z-20 overflow-y-auto">
      <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{titles[activeTab]}</h2>
        <div className="flex items-center gap-2">
          {project?.projectMode === 'seeded' && activeTab !== 'seed' && (
            <button className="text-xs text-gray-400 hover:text-white" onClick={() => onTabChange('seed')}>Seed</button>
          )}
          {activeTab !== 'issues' && (
            <button className="text-xs text-gray-400 hover:text-white" onClick={() => onTabChange('issues')}>Issues</button>
          )}
          {activeTab !== 'checklist' && (
            <button className="text-xs text-gray-400 hover:text-white" onClick={() => onTabChange('checklist')}>Checklist</button>
          )}
          {activeTab !== 'design' && (
            <button className="text-xs text-gray-400 hover:text-white" onClick={() => onTabChange('design')}>Design-Around</button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none ml-2" aria-label="Close">×</button>
        </div>
      </div>
      <div className="px-6 py-4 text-sm text-gray-200">
        {activeTab === 'seed' && <SeedTab project={project} />}
        {activeTab === 'issues' && <IssuesTab issues={issues} />}
        {activeTab === 'checklist' && <ChecklistTab items={checklist} />}
        {activeTab === 'design' && <DesignTab analysis={designAround} />}
      </div>
    </div>
  );
}

function SeedTab({ project }) {
  const sections = project?.seedSections || {};
  const inventors = project?.inventors || [];
  const priorFilings = project?.priorFilings || [];
  const report = project?.normalizationReport || null;
  if (project?.projectMode !== 'seeded') {
    return <div className="text-gray-400 text-xs">This project was not created from a seed document.</div>;
  }
  return (
    <div className="space-y-5">
      {inventors.length > 0 && (
        <div>
          <div className="text-xs uppercase text-gray-500 mb-1">Inventors</div>
          <div className="flex flex-wrap gap-2">
            {inventors.map(name => (
              <span key={name} className="rounded-full bg-blue-900/40 text-blue-200 px-2.5 py-0.5 text-xs">{name}</span>
            ))}
          </div>
        </div>
      )}
      {priorFilings.length > 0 && (
        <div>
          <div className="text-xs uppercase text-gray-500 mb-1">Inventor Prior Filings</div>
          <ul className="space-y-1">
            {priorFilings.map(f => (
              <li key={f.name}>
                <span className="font-mono text-xs text-gray-100">{f.name}</span>
                <span className="text-gray-400 text-xs"> — {f.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {report && (
        <div className="bg-gray-900/60 border border-gray-800 rounded p-3 space-y-3">
          <div className="text-xs uppercase text-gray-500">Normalization Report</div>
          {report.seed_summary && <div className="text-xs text-gray-300">{report.seed_summary}</div>}
          {Array.isArray(report.acknowledgments) && report.acknowledgments.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200">
                Acknowledgments ({report.acknowledgments.length}) — not to be attacked
              </summary>
              <ul className="mt-2 list-disc list-inside space-y-1 text-xs">
                {report.acknowledgments.map((a, i) => (
                  <li key={i}><strong>{a.topic}</strong>{a.guidance ? ` — ${a.guidance}` : ''}</li>
                ))}
              </ul>
            </details>
          )}
          {Array.isArray(report.checklist_prescores) && (
            <details>
              <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200">
                16-Item Checklist Prescore (pre-round)
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {report.checklist_prescores.map(cs => (
                  <li key={cs.item_id} className={cs.satisfied_by_seed ? 'text-green-300' : 'text-gray-300'}>
                    {cs.satisfied_by_seed ? '✓' : '○'} <strong>#{cs.item_id}:</strong> {cs.item_text}
                    <div className="pl-5 text-gray-500">{cs.reason} <span className="uppercase text-[10px]">({cs.confidence})</span></div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {Object.keys(sections).length > 0 && (
        <div>
          <div className="text-xs uppercase text-gray-500 mb-2">Parsed Sections</div>
          <div className="space-y-2">
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              sections[key] ? (
                <details key={key} className="rounded border border-gray-800">
                  <summary className="px-3 py-2 cursor-pointer hover:bg-gray-900 text-sm font-medium">{label}</summary>
                  <pre className="px-3 pb-3 text-xs whitespace-pre-wrap text-gray-300 font-sans">{sections[key]}</pre>
                </details>
              ) : null
            ))}
          </div>
        </div>
      )}
      {project?.seedDocument && (
        <details className="rounded border border-gray-800">
          <summary className="px-3 py-2 cursor-pointer hover:bg-gray-900 text-sm font-medium">
            Raw seed document ({project.seedDocument.length.toLocaleString()} chars)
          </summary>
          <pre className="px-3 pb-3 text-[11px] whitespace-pre-wrap text-gray-400 font-mono">{project.seedDocument}</pre>
        </details>
      )}
    </div>
  );
}

const SEVERITY_STYLES = {
  CRITICAL: 'bg-red-900/60 text-red-200 border-red-700',
  'MAJOR-A': 'bg-orange-900/50 text-orange-200 border-orange-700',
  'MAJOR-N': 'bg-yellow-900/40 text-yellow-200 border-yellow-700',
  MINOR: 'bg-blue-900/40 text-blue-200 border-blue-800',
  ADVISORY: 'bg-gray-800 text-gray-300 border-gray-700'
};

function IssuesTab({ issues }) {
  if (!issues || issues.length === 0) {
    return <div className="text-gray-400 text-xs">No issues raised yet.</div>;
  }
  const bySeverity = ['CRITICAL', 'MAJOR-A', 'MAJOR-N', 'MINOR', 'ADVISORY'];
  const groups = bySeverity.map(sev => ({
    sev,
    items: issues.filter(i => i.severity === sev)
  })).filter(g => g.items.length > 0);

  return (
    <div className="space-y-4">
      {groups.map(g => (
        <div key={g.sev}>
          <div className="text-xs uppercase text-gray-500 mb-2">{g.sev} ({g.items.length})</div>
          <ul className="space-y-1.5">
            {g.items.map(iss => (
              <li key={iss.id} className={`border rounded px-2.5 py-1.5 text-xs ${SEVERITY_STYLES[g.sev]}`}>
                <div>{iss.description}</div>
                <div className="text-[11px] opacity-70 mt-0.5">
                  Round {iss.round_num} · {iss.source_agent} · {iss.status || 'OPEN'}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ChecklistTab({ items }) {
  if (!items || items.length === 0) {
    return <div className="text-gray-400 text-xs">Checklist evaluation runs at convergence.</div>;
  }
  return (
    <ul className="space-y-2">
      {items.sort((a, b) => a.item_num - b.item_num).map(it => {
        const color =
          it.result === 'PASS' ? 'text-green-300' :
          it.result === 'FAIL' ? 'text-red-300' : 'text-gray-400';
        return (
          <li key={it.item_num} className="border border-gray-800 rounded px-3 py-2 text-xs">
            <div className={`font-medium ${color}`}>
              [{it.result}] #{it.item_num}: {it.item_text}
            </div>
            {it.reason && <div className="mt-1 text-gray-400">{it.reason}</div>}
          </li>
        );
      })}
    </ul>
  );
}

function DesignTab({ analysis }) {
  if (!analysis || (!analysis.strategies && !analysis.overall_robustness)) {
    return <div className="text-gray-400 text-xs">Design-around analysis runs at convergence.</div>;
  }
  return (
    <div className="space-y-4">
      {analysis.overall_robustness && (
        <div className="bg-gray-900/60 border border-gray-800 rounded p-3 text-xs text-gray-200">
          <div className="uppercase text-[10px] text-gray-500 mb-1">Overall robustness</div>
          {analysis.overall_robustness}
        </div>
      )}
      {Array.isArray(analysis.strategies) && analysis.strategies.map((s, i) => {
        const vColor =
          s.viability === 'HIGH' ? 'text-red-300' :
          s.viability === 'MEDIUM' ? 'text-amber-300' : 'text-gray-400';
        return (
          <div key={i} className="border border-gray-800 rounded p-3 text-xs space-y-1">
            <div className="font-medium text-gray-100">{s.title || `Strategy ${i + 1}`}</div>
            <div className={`text-[11px] ${vColor}`}>Viability: {s.viability || 'UNKNOWN'}</div>
            {s.description && <div><span className="text-gray-500">Approach:</span> {s.description}</div>}
            {s.avoids_infringement_because && <div><span className="text-gray-500">Avoids because:</span> {s.avoids_infringement_because}</div>}
            {s.counter_claim_language && <div><span className="text-gray-500">Counter claim:</span> {s.counter_claim_language}</div>}
          </div>
        );
      })}
    </div>
  );
}

function MessageCard({ msg }) {
  return (
    <div
      className={`p-4 rounded-lg ${
        msg.side === 'writer'
          ? 'bg-blue-900/30 ml-0 mr-auto max-w-3xl'
          : msg.side === 'adversary'
          ? 'bg-red-900/30 mr-0 ml-auto max-w-3xl'
          : 'bg-gray-800 max-w-4xl mx-auto'
      }`}
    >
      <div className="flex items-center mb-2">
        <span className={`font-bold ${AGENT_COLORS[msg.agentId] || 'text-gray-300'}`}>
          {AGENT_NAMES[msg.agentId] || msg.agentId}
        </span>
        <span className="ml-2 text-xs text-gray-500">Round {msg.roundNum}</span>
        {msg.phase && (
          <span className="ml-2 text-xs text-gray-500">({msg.phase})</span>
        )}
      </div>
      <div className="text-sm text-gray-300 whitespace-pre-wrap">{msg.content}</div>
    </div>
  );
}

function CommentComposer({ comment, setComment, visibility, setVisibility, maxLength = 5000, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-200 mb-1">
        Optional comment for the next round
      </label>
      <textarea
        rows={4}
        maxLength={maxLength}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholder || 'Redirect the council, flag a concern, or leave blank and press Continue...'}
        className="block w-full sm:text-sm border-gray-600 rounded-md px-3 py-2 border bg-gray-900 text-gray-100 focus:ring-blue-500 focus:border-blue-500"
      />
      <div className="mt-2 flex items-center gap-4 text-xs text-gray-300">
        <span>Visibility:</span>
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            name="visibility"
            value="all"
            checked={visibility === 'all'}
            onChange={() => setVisibility('all')}
          />
          <span>All agents</span>
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            name="visibility"
            value="mindblower"
            checked={visibility === 'mindblower'}
            onChange={() => setVisibility('mindblower')}
          />
          <span>Mindblower only (private)</span>
        </label>
        <span className="ml-auto text-gray-500">{comment.length} / {maxLength}</span>
      </div>
    </div>
  );
}

function IssueCountsRow({ issues }) {
  if (!issues) return null;
  const items = [
    ['CRITICAL', issues.CRITICAL, 'text-red-400'],
    ['MAJOR-A', issues['MAJOR-A'], 'text-orange-400'],
    ['MAJOR-N', issues['MAJOR-N'], 'text-yellow-400'],
    ['MINOR', issues.MINOR, 'text-blue-300'],
    ['ADVISORY', issues.ADVISORY, 'text-gray-400']
  ];
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      {items.map(([label, count, cls]) => (
        <span key={label} className={cls}>
          <span className="font-semibold">{label}:</span> {count ?? 0}
        </span>
      ))}
    </div>
  );
}

function InterRoundPanel({ state, onContinue }) {
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const atCap = state.effectiveCap != null && state.roundNum >= state.effectiveCap;

  const handle = async () => {
    setSubmitting(true);
    try { await onContinue({ comment, visibility }); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold">Round {state.roundNum} complete — paused for review</h2>
        <p className="text-sm text-gray-400 mt-1">
          Score: <span className="font-semibold text-gray-200">{state.score}</span> · Cap:{' '}
          <span className="font-semibold text-gray-200">{state.effectiveCap}</span>
          {state.recommendation && (
            <> · Current recommendation: <span className="text-green-400">{state.recommendation}</span></>
          )}
        </p>
        {atCap && (
          <p className="mt-2 text-xs text-amber-300">
            At cap — Continue will extend by 5 rounds and proceed.
          </p>
        )}
      </div>
      <IssueCountsRow issues={state.issues} />
      <CommentComposer
        comment={comment} setComment={setComment}
        visibility={visibility} setVisibility={setVisibility}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handle}
          disabled={submitting}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-md font-medium"
        >
          {submitting ? 'Starting...' : (atCap ? 'Extend cap and Continue' : 'Continue to Next Round')}
        </button>
      </div>
    </div>
  );
}

function PausedPanel({ state, onResume }) {
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    setSubmitting(true);
    try { await onResume({ comment, visibility }); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-amber-300">Session paused mid-round</h2>
        <p className="text-sm text-gray-300 mt-1">
          Round {state?.round_num}, {state?.phase} phase. {state?.completed_agent_ids?.length || 0} agent(s) already spoke this round.
          Remaining agents will resume after you continue.
        </p>
      </div>
      <CommentComposer
        comment={comment} setComment={setComment}
        visibility={visibility} setVisibility={setVisibility}
        placeholder="Inject a note for the remaining agents in this round..."
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handle}
          disabled={submitting}
          className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-md font-medium"
        >
          {submitting ? 'Resuming...' : 'Resume Round'}
        </button>
      </div>
    </div>
  );
}

function CapReachedPanel({ state, onExtend, onFinalize }) {
  const [addN, setAddN] = useState(3);
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const v = state.verdict || {};

  const handleExtend = async () => {
    setSubmitting(true);
    try { await onExtend({ add: parseInt(addN, 10), comment, visibility }); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold">Round cap reached</h2>
        <p className="text-sm text-gray-300 mt-1">
          Score: <span className="font-semibold">{state.score}</span> · Recommendation:{' '}
          <span className="text-green-400">{state.recommendation}</span>
        </p>
      </div>
      {v.rationale && (
        <div className="bg-gray-900/50 rounded p-3 text-sm">
          <div className="text-gray-400 text-xs uppercase mb-1">Viability judgment</div>
          <div className="text-gray-200">{v.rationale}</div>
        </div>
      )}
      {Array.isArray(v.design_around_suggestions) && v.design_around_suggestions.length > 0 && (
        <div className="bg-gray-900/50 rounded p-3 text-sm">
          <div className="text-gray-400 text-xs uppercase mb-1">Suggested design-arounds</div>
          <ul className="list-disc list-inside space-y-1 text-gray-200">
            {v.design_around_suggestions.map((s, i) => (
              <li key={i}><strong>{s.issue_description}:</strong> {s.suggested_approach}</li>
            ))}
          </ul>
        </div>
      )}
      <IssueCountsRow issues={state.issues} />
      <div className="border-t border-blue-900/70 pt-4">
        <label className="block text-sm font-medium text-gray-200 mb-1">
          Extend the cap and keep going?
        </label>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Add</span>
          <input
            type="number"
            min={1}
            value={addN}
            onChange={(e) => setAddN(e.target.value)}
            className="w-20 border-gray-600 rounded-md px-2 py-1 border bg-gray-900 text-gray-100"
          />
          <span className="text-gray-400">more rounds</span>
        </div>
        <div className="mt-3">
          <CommentComposer
            comment={comment} setComment={setComment}
            visibility={visibility} setVisibility={setVisibility}
            placeholder="Guide the next batch of rounds..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onFinalize}
          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
        >
          Accept cap, view results
        </button>
        <button
          type="button"
          onClick={handleExtend}
          disabled={submitting}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-md font-medium"
        >
          {submitting ? 'Extending...' : `Extend by ${addN || 0} and Continue`}
        </button>
      </div>
    </div>
  );
}

function NotViablePanel({ state, onExtend, onAccept }) {
  const [addN, setAddN] = useState(3);
  const [comment, setComment] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const v = state.verdict || {};

  const handleExtend = async () => {
    setSubmitting(true);
    try { await onExtend({ add: parseInt(addN, 10), comment, visibility }); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-red-300">Filing recommendation: NOT VIABLE</h2>
        <p className="text-sm text-gray-300 mt-1">
          The viability judge concluded that one or more unresolved objections cannot be designed around.
        </p>
      </div>
      {v.rationale && (
        <div className="bg-gray-900/50 rounded p-3 text-sm">
          <div className="text-gray-400 text-xs uppercase mb-1">Rationale</div>
          <div className="text-gray-200">{v.rationale}</div>
        </div>
      )}
      {Array.isArray(v.fatal_issues) && v.fatal_issues.length > 0 && (
        <div className="bg-gray-900/50 rounded p-3 text-sm">
          <div className="text-gray-400 text-xs uppercase mb-1">Fatal issues</div>
          <ul className="list-disc list-inside space-y-1 text-gray-200">
            {v.fatal_issues.map((f, i) => (
              <li key={i}>
                <strong>[{f.severity}]</strong> {f.description} — <span className="text-gray-400">{f.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="border-t border-red-900/70 pt-4">
        <p className="text-sm text-gray-300 mb-2">
          If you disagree with this verdict, you can extend the cap and continue trying to design around the objections.
        </p>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Add</span>
          <input
            type="number"
            min={1}
            value={addN}
            onChange={(e) => setAddN(e.target.value)}
            className="w-20 border-gray-600 rounded-md px-2 py-1 border bg-gray-900 text-gray-100"
          />
          <span className="text-gray-400">more rounds</span>
        </div>
        <div className="mt-3">
          <CommentComposer
            comment={comment} setComment={setComment}
            visibility={visibility} setVisibility={setVisibility}
            placeholder="Tell the council how to attack the fatal objections..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onAccept}
          className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
        >
          Accept NOT VIABLE, view results
        </button>
        <button
          type="button"
          onClick={handleExtend}
          disabled={submitting}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-md font-medium"
        >
          {submitting ? 'Extending...' : `Override — extend by ${addN || 0}`}
        </button>
      </div>
    </div>
  );
}

function InitialDraftPanel({ draft, conceptBrief, researchReport, onContinue }) {
  const [tab, setTab] = useState('draft');
  if (!draft) return null;
  const diagrams = Array.isArray(draft.diagrams) ? draft.diagrams : [];
  return (
    <div className="bg-emerald-900/20 border border-emerald-700 rounded-lg p-6 space-y-4">
      <div>
        <h2 className="text-lg font-bold text-emerald-200">Phase 0 complete — initial draft ready</h2>
        <p className="text-sm text-gray-300 mt-1">
          Concept brief, prior-art research, and an initial patent draft (version {draft.version}) are ready for review.
          Click Continue when you're ready to start the adversarial review loop.
        </p>
      </div>
      <div className="flex gap-2 text-sm">
        <button onClick={() => setTab('draft')} className={`px-3 py-1 rounded ${tab === 'draft' ? 'bg-emerald-700 text-white' : 'bg-gray-800 text-gray-300'}`}>Draft</button>
        {diagrams.length > 0 && (
          <button onClick={() => setTab('diagrams')} className={`px-3 py-1 rounded ${tab === 'diagrams' ? 'bg-emerald-700 text-white' : 'bg-gray-800 text-gray-300'}`}>Diagrams ({diagrams.length})</button>
        )}
        <button onClick={() => setTab('brief')} className={`px-3 py-1 rounded ${tab === 'brief' ? 'bg-emerald-700 text-white' : 'bg-gray-800 text-gray-300'}`}>Concept Brief</button>
        <button onClick={() => setTab('research')} className={`px-3 py-1 rounded ${tab === 'research' ? 'bg-emerald-700 text-white' : 'bg-gray-800 text-gray-300'}`}>Research</button>
      </div>
      <div className="bg-gray-900/50 rounded p-4 max-h-[36rem] overflow-y-auto text-sm">
        {tab === 'draft' && <DraftViewer draft={draft} />}
        {tab === 'diagrams' && <DiagramsViewer diagrams={diagrams} />}
        {tab === 'brief' && <ConceptBriefViewer brief={conceptBrief} />}
        {tab === 'research' && <ResearchViewer report={researchReport} />}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-md font-medium"
        >
          Continue to Round 1
        </button>
      </div>
    </div>
  );
}

function DiagramsViewer({ diagrams }) {
  if (!diagrams || diagrams.length === 0) return <div className="text-gray-400">No diagrams generated.</div>;
  return (
    <div className="space-y-6">
      {diagrams.map((d, i) => (
        <div key={d.id || i} className="space-y-2">
          <div className="text-emerald-300 font-semibold uppercase text-xs">{d.title}</div>
          <MermaidDiagram source={d.mermaid} idHint={d.id || `fig${i + 1}`} />
          {d.caption && <div className="text-gray-400 text-xs italic">{d.caption}</div>}
        </div>
      ))}
    </div>
  );
}

function MermaidDiagram({ source, idHint }) {
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const id = `m-${idHint || 'd'}-${Math.random().toString(36).slice(2, 8)}`;
    loadMermaid()
      .then(mermaid => mermaid.render(id, source))
      .then(({ svg }) => { if (!cancelled) { setSvg(svg); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [source, idHint]);

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded p-3 text-xs">
        <div className="text-red-300 mb-2">Mermaid render failed: {error}</div>
        <pre className="text-gray-400 whitespace-pre-wrap">{source}</pre>
      </div>
    );
  }
  return (
    <div
      className="bg-gray-800 rounded p-4 overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function DraftViewer({ draft }) {
  if (!draft) return <div className="text-gray-400">No draft yet.</div>;
  const claims = Array.isArray(draft.claims) ? draft.claims : [];
  return (
    <div className="space-y-4 text-gray-200">
      {draft.abstract && <Section title="Abstract" body={draft.abstract} />}
      {draft.field_of_invention && <Section title="Field of Invention" body={draft.field_of_invention} />}
      {draft.background && <Section title="Background" body={draft.background} />}
      {draft.summary && <Section title="Summary" body={draft.summary} />}
      {draft.detailed_description && <Section title="Detailed Description" body={draft.detailed_description} />}
      {draft.embodiments && <Section title="Embodiments" body={draft.embodiments} />}
      {claims.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-2">Claims</div>
          <ol className="space-y-2">
            {claims.map(c => (
              <li key={c.number} className="pl-4 border-l-2 border-gray-700">
                <span className="text-gray-400 text-xs">
                  {c.number}. ({c.type}{c.depends_on ? `, depends on ${c.depends_on}` : ''})
                </span>
                <div className="whitespace-pre-wrap">{c.text}</div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {draft.notes && <Section title="Drafting Notes" body={draft.notes} />}
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div>
      <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">{title}</div>
      <div className="whitespace-pre-wrap text-gray-200">{body}</div>
    </div>
  );
}

function ConceptBriefViewer({ brief }) {
  if (!brief) return <div className="text-gray-400">No concept brief.</div>;
  return (
    <div className="space-y-3 text-gray-200">
      {brief.unique_offering && <Section title="Unique Offering" body={brief.unique_offering} />}
      {brief.problem_solved && <Section title="Problem Solved" body={brief.problem_solved} />}
      {Array.isArray(brief.core_innovations) && brief.core_innovations.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Core Innovations</div>
          <ul className="list-disc list-inside space-y-1">
            {brief.core_innovations.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {brief.market_context && <Section title="Market Context" body={brief.market_context} />}
      {brief.target_users && <Section title="Target Users" body={brief.target_users} />}
      {Array.isArray(brief.candidate_claim_themes) && brief.candidate_claim_themes.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Candidate Claim Themes</div>
          <ul className="list-disc list-inside space-y-1">
            {brief.candidate_claim_themes.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
      {brief.eligibility_pre_assessment && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">
            Alice §101 Pre-Assessment ({brief.eligibility_pre_assessment.alice_risk})
          </div>
          <div className="whitespace-pre-wrap">{brief.eligibility_pre_assessment.rationale}</div>
        </div>
      )}
      {Array.isArray(brief.open_questions) && brief.open_questions.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Open Questions</div>
          <ol className="list-decimal list-inside space-y-1">
            {brief.open_questions.map((q, i) => <li key={i}>{q}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
}

function ResearchViewer({ report }) {
  if (!report) return <div className="text-gray-400">No research report.</div>;
  const cats = report.prior_art_landscape?.categories || [];
  const cpcs = report.prior_art_landscape?.cpc_codes || [];
  const archs = report.prior_art_landscape?.key_reference_archetypes || [];
  const wkrnds = report.design_around_space?.competitor_workarounds || [];
  const opps = report.design_around_space?.claim_strengthening_opportunities || [];
  return (
    <div className="space-y-3 text-gray-200">
      {report.research_summary && <Section title="Summary" body={report.research_summary} />}
      {cpcs.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">CPC Codes</div>
          <div className="flex flex-wrap gap-2">
            {cpcs.map((c, i) => <span key={i} className="bg-gray-800 px-2 py-1 rounded text-xs">{c}</span>)}
          </div>
        </div>
      )}
      {cats.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Prior-Art Categories</div>
          <ul className="space-y-2">
            {cats.map((c, i) => (
              <li key={i}>
                <strong>{c.name}:</strong> {c.description}
                {c.candidate_claim_themes_at_risk?.length > 0 && (
                  <div className="text-xs text-amber-300 mt-1">
                    At risk: {c.candidate_claim_themes_at_risk.join('; ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {archs.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Reference Archetypes</div>
          <ul className="list-disc list-inside space-y-1">
            {archs.map((a, i) => <li key={i}><strong>{a.archetype}:</strong> {a.why_relevant}</li>)}
          </ul>
        </div>
      )}
      {wkrnds.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Design-Around Space</div>
          <ul className="space-y-2">
            {wkrnds.map((w, i) => (
              <li key={i}>
                <span className="text-amber-300">[{w.viability}]</span> <strong>{w.approach}</strong>
                {w.claim_blockers_needed?.length > 0 && (
                  <div className="text-xs text-gray-400 mt-1">
                    Blockers: {w.claim_blockers_needed.join('; ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {opps.length > 0 && (
        <div>
          <div className="text-emerald-300 font-semibold uppercase text-xs mb-1">Claim Strengthening Opportunities</div>
          <ul className="list-disc list-inside space-y-1">
            {opps.map((o, i) => <li key={i}>{o}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
